import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { LoanBookView } from '../types/loanBookView';

interface BrokerReportClawbackEvent {
  loanId: string;
  customerName: string;
  clawbackAmountCents: number;
  originalSettlementDate: string;
  clawbackDate: string;
  lender: string;
}

interface BrokerReportLoan {
  loanId: string;
  customerName: string;
  lender: string;
  settlementDate: string;
  loanAmountCents: number;
  source: 'retained' | 'new';
}

export interface BrokerReportData {
  brokerName: string;
  reportDate: string;
  companyName: string;
  executiveSummary: {
    upfrontCents: number;
    trailCents: number;
    loanBookCents: number;
    lineOfTrail: number;
    period: string;
  };
  allTimeSummary: {
    totalLoans: number;
    totalCommissionCents: number;
    averageLoanSizeCents: number;
    earliestYear: number;
  };
  bookHealth: {
    activeLoans: number;
    dischargedLoans: number;
    clawbackEvents: number;
    clawbackTotalCents: number;
    arrearsLoans: number;
  };
  commissionTrend: Array<{
    month: string;
    upfrontCents: number;
    trailCents: number;
  }>;
  lenderConcentration: Array<{
    lender: string;
    loanCount: number;
    percentage: number;
  }>;
  templateBreakdown: Array<{
    templateId: string;
    templateName: string;
    officeName: string;
    loanCount: number;
    upfrontCents: number;
    trailCents: number;
    totalCents: number;
    firstRunDate: string;
    lastRunDate: string;
  }>;
  clawbackEventsList: BrokerReportClawbackEvent[];
  dischargedLoansList: BrokerReportLoan[];
  acquiredBookRetention?: {
    acquiredTemplateName: string;
    dischargedCount: number;
    retainedCount: number;
    retainedValueCents: number;
    retentionRate: number;
  };
  retainedLoans: BrokerReportLoan[];
  newLoans: BrokerReportLoan[];
  portfolioSummary: {
    retainedCount: number;
    retainedValueCents: number;
    newCount: number;
    newValueCents: number;
    totalCount: number;
    totalValueCents: number;
  };
  /**
   * Scoped loan-book panel. ABSENT unless the tenant has
   * `featureFlags.loan_book_scoped_view = 'enforce'`, and absent whenever the
   * backend could not build it honestly. Absent MUST render the legacy card.
   */
  loanBookView?: LoanBookView;
}

interface UseBrokerReportReturn {
  data: BrokerReportData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBrokerReport(companyId: string, brokerDisplay: string): UseBrokerReportReturn {
  const [data, setData] = useState<BrokerReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  useEffect(() => {
    if (!companyId || !brokerDisplay) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const fn = httpsCallable(functions, 'getBrokerReport');
        const result = await fn({ companyId, brokerDisplay });
        setData(result.data as BrokerReportData);
      } catch (err: any) {
        console.error('[useBrokerReport] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch broker report');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, brokerDisplay, refetchTrigger]);

  return { data, loading, error, refetch };
}
